import React, { useState } from "react";
import { toast } from "sonner";
import { Minus, Plus, Send } from "lucide-react";
import { api, formatErr } from "../../lib/api";
import { localToday, tzOffset } from "../../lib/wellness";
import { ScaleRow } from "./ScaleRow";
import { PainSection } from "./PainSection";

const INIT = { sleep_hours: 8, sleep_quality: null, stress: null, muscle_fatigue: null, general_fatigue: null, mood: null, readiness_to_train: null, pain: null, pain_zones: [], pain_onset: null, pain_type: null, trained_yesterday: null, rpe_last_session: null, comment: "" };
const REQ = ["sleep_quality", "stress", "muscle_fatigue", "general_fatigue", "mood", "readiness_to_train", "pain"];
const Sec = ({ t, children }) => <div className="qv-card-app" style={{ padding: 16, marginBottom: 12 }}><div className="qv-overline" style={{ marginBottom: 12 }}>{t}</div>{children}</div>;

const Sleep = ({ v, set }) => (
  <div style={{ marginBottom: 18 }} data-testid="wb-sleep-hours">
    <div style={{ fontWeight: 700, fontSize: 15 }}>Horas de sueño de anoche</div>
    <div style={{ display: "flex", alignItems: "center", gap: 14, marginTop: 8 }}>
      <button type="button" className="qv-btn-ghost" style={{ padding: 12 }} onClick={() => set(Math.max(0, v - 0.5))} data-testid="wb-sleep-minus"><Minus size={16} /></button>
      <div style={{ fontFamily: "Bebas Neue", fontSize: 44, minWidth: 90, textAlign: "center" }} data-testid="wb-sleep-value">{v} h</div>
      <button type="button" className="qv-btn-ghost" style={{ padding: 12 }} onClick={() => set(Math.min(16, v + 0.5))} data-testid="wb-sleep-plus"><Plus size={16} /></button>
    </div>
  </div>
);

const Rpe = ({ ctx, f, set }) => {
  const ask = ctx || f.trained_yesterday;
  return (
    <Sec t="Tu última sesión">
      {ctx ? <div style={{ fontSize: 13, color: "#a3a3a3", marginBottom: 10 }}>Marcaste como hecha tu sesión del {new Date(`${ctx.date}T12:00:00`).toLocaleDateString("es-ES", { weekday: "long", day: "numeric", month: "long" })}{ctx.title ? ` (${ctx.title})` : ""}.</div> : (
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 8 }}>¿Entrenaste ayer?</div>
          <div style={{ display: "flex", gap: 8 }}>
            {[[true, "Sí"], [false, "No"]].map(([v, l]) => <button key={l} type="button" className={f.trained_yesterday === v ? "qv-btn-primary" : "qv-btn-ghost"} style={{ flex: 1 }} onClick={() => set({ trained_yesterday: v, rpe_last_session: v ? f.rpe_last_session : null })} data-testid={`wb-trained-${v ? "yes" : "no"}`}>{l}</button>)}
          </div>
        </div>
      )}
      {ask && <ScaleRow label="¿Qué esfuerzo te supuso? (RPE)" hint="1 = muy fácil · 10 = esfuerzo máximo" min={1} max={10} value={f.rpe_last_session} onChange={(v) => set({ rpe_last_session: v })} reverse ends={["Muy fácil", "Máximo"]} testid="wb-rpe" />}
    </Sec>
  );
};

export const WellnessForm = ({ rpeSession, onDone }) => {
  const [f, setF] = useState(INIT);
  const [busy, setBusy] = useState(false);
  const set = (o) => setF((x) => ({ ...x, ...o }));
  const missing = REQ.some((k) => f[k] === null) || (f.pain > 0 && (!f.pain_zones.length || !f.pain_type || !f.pain_onset))
    || (rpeSession ? !f.rpe_last_session : f.trained_yesterday === null || (f.trained_yesterday && !f.rpe_last_session));
  const send = () => {
    setBusy(true);
    api.post("/me/wellness", { ...f, comment: f.comment.trim() || null, local_date: localToday(), tz_offset_min: tzOffset() })
      .then((r) => onDone(r.data)).catch((e) => toast.error(formatErr(e))).finally(() => setBusy(false));
  };
  return (
    <div data-testid="wb-form">
      <Sec t="Descanso">
        <Sleep v={f.sleep_hours} set={(v) => set({ sleep_hours: v })} />
        <ScaleRow label="Calidad del sueño" value={f.sleep_quality} onChange={(v) => set({ sleep_quality: v })} ends={["Muy mala", "Muy buena"]} testid="wb-sleep-quality" />
      </Sec>
      <Sec t="Cómo te sientes">
        <ScaleRow label="Estrés" value={f.stress} onChange={(v) => set({ stress: v })} reverse ends={["Muy bajo", "Muy alto"]} testid="wb-stress" />
        <ScaleRow label="Fatiga muscular" value={f.muscle_fatigue} onChange={(v) => set({ muscle_fatigue: v })} reverse ends={["Nada", "Muy alta"]} testid="wb-muscle-fatigue" />
        <ScaleRow label="Fatiga general" value={f.general_fatigue} onChange={(v) => set({ general_fatigue: v })} reverse ends={["Nada", "Muy alta"]} testid="wb-general-fatigue" />
        <ScaleRow label="Estado de ánimo" value={f.mood} onChange={(v) => set({ mood: v })} ends={["Muy bajo", "Muy bueno"]} testid="wb-mood" />
        <ScaleRow label="Disposición para entrenar" value={f.readiness_to_train} onChange={(v) => set({ readiness_to_train: v })} ends={["Ninguna", "Con muchas ganas"]} testid="wb-readiness" />
      </Sec>
      <Sec t="Molestias"><PainSection f={f} set={set} /></Sec>
      <Rpe ctx={rpeSession} f={f} set={set} />
      <Sec t="Comentario (opcional)">
        <textarea className="qv-input" rows={2} maxLength={280} value={f.comment} onChange={(e) => set({ comment: e.target.value })} placeholder="Algo que deba saber tu entrenador" style={{ textTransform: "none" }} data-testid="wb-comment" />
      </Sec>
      <button className="qv-btn-primary" style={{ width: "100%", padding: 16, display: "flex", gap: 8, justifyContent: "center" }} disabled={missing || busy} onClick={send} data-testid="wb-submit">
        <Send size={16} /> {busy ? "Enviando…" : missing ? "Completa las respuestas" : "Enviar y ver mi día"}
      </button>
    </div>
  );
};
